/**
 * MarthaQuestionBlock Component
 * Custom BlockNote block for displaying AI-generated clarifying questions inline
 * Uses warm amber styling with Martha branding
 */

import { useState, useCallback } from 'react';
import { createReactBlockSpec } from '@blocknote/react';
import { Sparkles, Check, X, MessageSquare, Loader2 } from 'lucide-react';

// Status type for the question
export type MarthaQuestionStatus = 'pending' | 'answered' | 'dismissed';

// Props schema for the Martha Question block
export interface MarthaQuestionProps {
  questionId: string;
  question: string;
  context: string;
  status: MarthaQuestionStatus;
  answer?: string;
}

// Default props
const defaultProps: MarthaQuestionProps = {
  questionId: '',
  question: '',
  context: '',
  status: 'pending',
  answer: '',
};

/**
 * The React component for rendering the Martha Question block
 */
interface MarthaQuestionComponentProps {
  block: {
    id: string;
    props: MarthaQuestionProps;
  };
  // Editor is typed loosely to support any BlockNote schema
  editor: {
    updateBlock: (id: string, update: { props: Partial<MarthaQuestionProps> }) => void;
  };
}

function MarthaQuestionComponent({
  block,
  editor,
}: MarthaQuestionComponentProps) {
  const { questionId, question, context, status, answer } = block.props;
  const [isAnswering, setIsAnswering] = useState(false);
  const [answerText, setAnswerText] = useState(answer || '');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle answering the question
  const handleAnswer = useCallback(() => {
    setIsAnswering(true);
  }, []);

  // Handle submitting the answer
  const handleSubmitAnswer = useCallback(async () => {
    if (!answerText.trim()) return;

    setIsSubmitting(true);
    try {
      // Update the block with the answer
      editor.updateBlock(block.id, {
        props: {
          ...block.props,
          status: 'answered' as MarthaQuestionStatus,
          answer: answerText,
        },
      });
      setIsAnswering(false);
    } catch (error) {
      console.error('Failed to submit answer:', error);
    } finally {
      setIsSubmitting(false);
    }
  }, [editor, block, answerText]);

  // Handle dismissing the question
  const handleDismiss = useCallback(() => {
    editor.updateBlock(block.id, {
      props: {
        ...block.props,
        status: 'dismissed' as MarthaQuestionStatus,
      },
    });
  }, [editor, block]);

  // Handle canceling answer mode
  const handleCancelAnswer = useCallback(() => {
    setIsAnswering(false);
    setAnswerText(answer || '');
  }, [answer]);

  // Status-based styling
  const getStatusStyles = () => {
    switch (status) {
      case 'answered':
        return {
          container: 'bg-green-50 border-green-200',
          badge: 'bg-green-100 text-green-700',
          badgeText: 'Answered',
        };
      case 'dismissed':
        return {
          container: 'bg-stone-50 border-stone-200 opacity-60',
          badge: 'bg-stone-100 text-stone-500',
          badgeText: 'Dismissed',
        };
      default:
        return {
          container: 'bg-amber-50 border-amber-200',
          badge: 'bg-amber-100 text-amber-700',
          badgeText: 'Pending',
        };
    }
  };

  const styles = getStatusStyles();

  return (
    <div
      className={`rounded-xl border-2 p-4 my-3 transition-all duration-200 ${styles.container}`}
      data-question-id={questionId}
      data-status={status}
    >
      {/* Header with Martha avatar and status */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          {/* Martha avatar */}
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-200 text-amber-700">
            <Sparkles className="w-4 h-4" />
          </div>
          <div className="flex flex-col">
            <span className="text-sm font-semibold text-stone-800">
              Martha asks
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${styles.badge}`}>
              {styles.badgeText}
            </span>
          </div>
        </div>

        {/* Action buttons for pending status */}
        {status === 'pending' && !isAnswering && (
          <div className="flex items-center gap-1">
            <button
              onClick={handleAnswer}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium
                       text-amber-700 bg-amber-100 hover:bg-amber-200
                       rounded-lg transition-colors"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              Answer
            </button>
            <button
              onClick={handleDismiss}
              className="flex items-center gap-1 px-3 py-1.5 text-sm font-medium
                       text-stone-500 hover:bg-stone-100
                       rounded-lg transition-colors"
              title="Dismiss question"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        )}
      </div>

      {/* Question text */}
      <div className="mb-3">
        <p className="text-base font-medium text-stone-900 leading-relaxed">
          {question}
        </p>
      </div>

      {/* Context section */}
      {context && (
        <div className="mb-3 pl-3 border-l-2 border-amber-300">
          <span className="text-xs font-medium text-stone-500 uppercase tracking-wide">
            Context
          </span>
          <p className="text-sm text-stone-600 mt-1">{context}</p>
        </div>
      )}

      {/* Answer input section (when answering) */}
      {isAnswering && (
        <div className="mt-4 p-3 bg-white rounded-lg border border-amber-200">
          <label className="text-xs font-medium text-stone-500 uppercase tracking-wide block mb-2">
            Your Answer
          </label>
          <textarea
            value={answerText}
            onChange={(e) => setAnswerText(e.target.value)}
            placeholder="Type your response to Martha's question..."
            className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg resize-none
                     focus:outline-none focus:ring-1 focus:ring-amber-300 focus:border-amber-300
                     placeholder:text-stone-400"
            rows={3}
            autoFocus
          />
          <div className="flex items-center justify-end gap-2 mt-3">
            <button
              onClick={handleCancelAnswer}
              className="px-3 py-1.5 text-sm font-medium text-stone-500 hover:bg-stone-100
                       rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmitAnswer}
              disabled={!answerText.trim() || isSubmitting}
              className="flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium
                       text-white bg-amber-600 hover:bg-amber-700
                       rounded-lg transition-colors
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Check className="w-3.5 h-3.5" />
              )}
              Submit Answer
            </button>
          </div>
        </div>
      )}

      {/* Answered section (when answered) */}
      {status === 'answered' && answer && (
        <div className="mt-4 p-3 bg-green-100/50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 mb-2">
            <Check className="w-4 h-4 text-green-600" />
            <span className="text-xs font-medium text-green-700 uppercase tracking-wide">
              Your Answer
            </span>
          </div>
          <p className="text-sm text-stone-700">{answer}</p>
        </div>
      )}
    </div>
  );
}

/**
 * BlockNote schema definition for the Martha Question block
 */
export const marthaQuestionSchema = createReactBlockSpec(
  {
    type: 'marthaQuestion' as const,
    propSchema: {
      questionId: {
        default: defaultProps.questionId,
      },
      question: {
        default: defaultProps.question,
      },
      context: {
        default: defaultProps.context,
      },
      status: {
        default: defaultProps.status as string,
        values: ['pending', 'answered', 'dismissed'] as const,
      },
      answer: {
        default: defaultProps.answer || '',
      },
    },
    content: 'none',
  },
  {
    render: (props) => {
      // Cast props appropriately for our component
      const blockProps = {
        block: {
          id: props.block.id,
          props: props.block.props as unknown as MarthaQuestionProps,
        },
        editor: props.editor as MarthaQuestionComponentProps['editor'],
      };
      return <MarthaQuestionComponent {...blockProps} />;
    },
  }
);

/**
 * Export the block component for direct use
 */
export const MarthaQuestionBlock = MarthaQuestionComponent;

/**
 * Helper function to insert a Martha question block
 * @param editor - The BlockNote editor instance
 * @param props - Partial props for the Martha question
 */
export function insertMarthaQuestion(
  editor: {
    insertBlocks: (
      blocks: Array<{ type: string; props: Record<string, unknown> }>,
      referenceBlock: unknown,
      placement: 'before' | 'after'
    ) => void;
    getTextCursorPosition: () => { block: unknown };
  },
  props: Partial<MarthaQuestionProps>
): void {
  const questionId = props.questionId || `mq-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  editor.insertBlocks(
    [
      {
        type: 'marthaQuestion',
        props: {
          questionId,
          question: props.question || '',
          context: props.context || '',
          status: props.status || 'pending',
          answer: props.answer || '',
        },
      },
    ],
    editor.getTextCursorPosition().block,
    'after'
  );
}
